import SolarSystem from '../main/objects/system';
import { OrbitingBody } from '../main/objects/body';
import Transfer from '../main/objects/transfer';

import MissionControls from '../components/Transfer/MissionControls'
import PorkchopPlot from '../components/Transfer/PorkChopPlot';
import OrbitDisplayTabs from '../components/Transfer/OrbitDisplayTabs';
import TransferInfo from '../components/Transfer/TransferInfo';
import HelpCollapse from '../components/Transfer/HelpCollapse';
import Navbar from '../components/Navbar';
import DashboardPanel from '../components/DashboardPanel';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import ScanningOverlay from '../components/ScanningOverlay';

import { isInvalidOrbitInput, porkchopInputsFromUI } from '../utils';

import React, { useState, useEffect } from "react";
import CssBaseline from '@mui/material/CssBaseline';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Collapse from '@mui/material/Collapse';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import IconButton from '@mui/material/IconButton';


import { useAtom } from 'jotai';
import { porkchopInputsAtom, systemAtom, timeSettingsAtom, transferAtom, transferEarlyStartDateAtom, transferEndOrbitAtom, transferLateStartDateAtom, transferLongFlightTimeAtom, transferShortFlightTimeAtom, transferStartOrbitAtom, transferControlsOptionsAtom } from '../App';


export function blankTransfer(system: SolarSystem): Transfer {
  const orbit = (system.bodyFromId(1) as OrbitingBody).orbit;
  return new Transfer({
    system:                 system,
    startOrbit:             orbit,
    endOrbit:               orbit,
    startDate:              0.0,
    flightTime:             orbit.siderealPeriod,
    endDate:                orbit.siderealPeriod,
    transferTrajectory:     {orbits: [], intersectTimes: [0,0], maneuvers: []},
    ejections:              [],
    insertions:             [],
    maneuvers:              [],
    maneuverContexts:       [],
    deltaV:                 0.0,
    ejectionInsertionType:  "fastoberth",
    planeChange:            false,
    matchStartMo:           true,
    matchEndMo:             false,
    noInsertionBurn:        false,
    soiPatchPositions:      [],
    patchPositionError:     0,
    patchTimeError:         0,
  })
}

////////// App Content //////////

function TransferAppContent() { 
  const [system] = useAtom(systemAtom);
  const [timeSettings] = useAtom(timeSettingsAtom);
  const [transfer, setTransfer] = useAtom(transferAtom);
  const [, setPorkchopInputs] = useAtom(porkchopInputsAtom);

  const [startOrbit] = useAtom(transferStartOrbitAtom);
  const [endOrbit] = useAtom(transferEndOrbitAtom);

  const [earlyStartDate] = useAtom(transferEarlyStartDateAtom);
  const [lateStartDate] = useAtom(transferLateStartDateAtom);
  const [shortFlightTime] = useAtom(transferShortFlightTimeAtom);
  const [longFlightTime] = useAtom(transferLongFlightTimeAtom)
  const [controlsOptionsState] = useAtom(transferControlsOptionsAtom);

  const [invalidInput, setInvalidInput] = useState(false);
  const [porkCalculating, setPorkCalculating] = useState(false);
  const [plotCount, setPlotCount] = useState(0);
  const [showHelp, setShowHelp] = useState(false);

  // UI State
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [plotOpen, setPlotOpen] = useState(false);

  function handlePlotButtonPress() {
    // ensure there are no invalid orbit inputs
    const invalidFlag = isInvalidOrbitInput(startOrbit.orbit) || isInvalidOrbitInput(endOrbit.orbit);
    
    // display a warning and do not calculate a Porkchop if the inputs are invalid
    setInvalidInput(invalidFlag);
    if(invalidFlag) {
      setLeftOpen(true);
      return
    }

    // prepare porkchop inputs
    let porkInputs: PorkchopInputs;
    try{
      porkInputs = porkchopInputsFromUI(system, startOrbit.orbit, endOrbit.orbit, earlyStartDate, lateStartDate, shortFlightTime, longFlightTime, controlsOptionsState, timeSettings);
    } catch {
      setInvalidInput(true);
      return
    }
    setPorkchopInputs(porkInputs);
    setPlotCount(plotCount + 1);
    setPlotOpen(true); // Auto-open plot analysis
    console.log('"Plot!" button pressed.');
  }

  useEffect(() => {
    if(transfer.deltaV > 0) {
      setRightOpen(true);
    }
  }, [transfer.deltaV]);

  useEffect(() => {
    if(plotCount > 0 || transfer.deltaV === 0) {
      setTransfer(blankTransfer(system));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [system])

  const companionButtons = (
    <IconButton 
        size="small"
        onClick={() => setPlotOpen(!plotOpen)} 
        style={{ 
            backgroundColor: plotOpen ? 'rgba(0, 243, 255, 0.2)' : 'rgba(0, 243, 255, 0.1)',
            border: '1px solid rgba(0, 243, 255, 0.3)',
            backdropFilter: 'blur(4px)',
            color: '#00f3ff',
            pointerEvents: 'auto',
        }}
    >
        <EqualizerIcon />
    </IconButton>
  );

  ///// App Body /////
  return (
    <div style={{ width: '100vw', height: '100vh', position: 'relative', overflow: 'hidden' }}>
      <CssBaseline />

      {/* Background Layer: 3D Orbit Display */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
        <OrbitDisplayTabs />
      </div>

      <ScanningOverlay isCalculating={porkCalculating} />

      {/* UI Overlay Layer */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'none', display: 'flex', flexDirection: 'column' }}>
        <Navbar showHelp={showHelp} setShowHelp={setShowHelp} />
        
        {/* Floating Header */}
        <div style={{ margin: '16px 32px', pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div>
            <Typography variant="h4" color="primary" sx={{ fontWeight: 800, textShadow: '0 0 10px rgba(0, 243, 255, 0.5)' }}>
              TRANSFER PLANNER
            </Typography>
            <HelpCollapse showHelp={showHelp} />
            <Collapse in={invalidInput}>
              <Alert severity='error' sx={{ mt: 1 }}>
                <AlertTitle>Error</AlertTitle>
                Orbit inputs are invalid. Check Mission Parameters.
              </Alert>
            </Collapse>
          </div>
        </div>

        {/* Dashboard Panels */}
        <DashboardPanel 
          side="left" 
          title="Mission Parameters" 
          isOpen={leftOpen} 
          onToggle={() => setLeftOpen(!leftOpen)}
          width={340}
        >
          <MissionControls />
          <div style={{ marginTop: '24px', textAlign: 'center' }}>
            <LoadingButton 
              variant="contained" 
              fullWidth
              size="large"
              loadingPosition="end"
              endIcon={<RocketLaunchIcon />}
              loading={porkCalculating}
              onClick={() => handlePlotButtonPress()}
              sx={{ py: 1.5 }}
            >
              Initialise Calculation
            </LoadingButton>
          </div>
        </DashboardPanel>

        <DashboardPanel
          side="center"
          title="Porkchop Plot Analysis"
          isOpen={plotOpen}
          onToggle={() => setPlotOpen(false)}
          width={700}
          height={600}
        >
          <PorkchopPlot 
            plotCount={plotCount}
            setCalculating={setPorkCalculating}
          />
        </DashboardPanel>

        <DashboardPanel
          side="right"
          title="Telemetry Data"
          isOpen={rightOpen && transfer.deltaV > 0}
          onToggle={() => setRightOpen(!rightOpen)}
          width={380}
          companionButtons={companionButtons}
        >
          <TransferInfo />
        </DashboardPanel>
      </div>
    </div>
  )
}

function TransferApp() {
  return <>{TransferAppContent()}</>
}

export default React.memo(TransferApp);
